import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  TextField,
  Stack,
  InputAdornment,
  Typography,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { inventory } from "../../Static/inventory";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoIcon from "@mui/icons-material/Info";
import RepeatIcon from "@mui/icons-material/Repeat";

export default function ChooseStirForm({ task, onSubmit }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(task.quantity || "");
  const [error, setError] = useState("");
  const [itemError, setItemError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inventoryKey = task.type;
  const minQuantity = 1;
  const maxQuantity = 10;

  // Initialize selectedItem with the stored value if it exists
  useEffect(() => {
    if (task.item_name) {
      const item = inventory[inventoryKey].find(
        (item) => item.item_name === task.item_name
      );
      setSelectedItem(item || null);
    }
  }, [task.item_name, inventoryKey]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // Convert quantity to number to ensure proper data type
        const numericQuantity = parseInt(quantity, 10);

        task.args = `${selectedItem.item_name} - ${numericQuantity} times`;
        task.quantity = numericQuantity; // Store as number, not string
        task.item_name = selectedItem.item_name;
        task.speed = selectedItem.speed;
        task.duration = numericQuantity * 50;

        console.log(
          "🔧 Stir form submitting with quantity:",
          numericQuantity,
          typeof numericQuantity
        );

        await onSubmit(task);
      } catch (error) {
        setError("Failed to submit. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const validateForm = () => {
    if (!selectedItem) {
      setItemError("Please select a stir type");
      setError("");
      return false;
    }
    if (!quantity) {
      setError("Please enter the number of times");
      setItemError("");
      return false;
    }
    if (+quantity < minQuantity || +quantity > maxQuantity) {
      setError(
        `Number of times must be between ${minQuantity} and ${maxQuantity}`
      );
      setItemError("");
      return false;
    }
    setError("");
    setItemError("");
    return true;
  };

  const resetForm = () => {
    setSelectedItem(null);
    setQuantity("");
    setError("");
    setItemError("");
  };

  return (
    <Fade in={true} timeout={500}>
      <Paper
        elevation={3}
        sx={{
          p: theme.spacing(3),
          borderRadius: 2,
          background: theme.palette.background.paper,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: theme.shadows[6],
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" fontWeight="medium" color="primary">
                Configure Stir Settings
              </Typography>
              <Tooltip title="Reset form">
                <IconButton size="small" onClick={resetForm} color="primary">
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Display useful information about stir types */}
            <Alert
              severity="info"
              icon={<InfoIcon />}
              sx={{
                borderRadius: 1,
                "& .MuiAlert-icon": {
                  color: theme.palette.info.main,
                },
              }}
            >
              Select a stir speed and specify how many times you want to stir
            </Alert>

            <Autocomplete
              value={selectedItem}
              onChange={(event, newValue) => {
                setSelectedItem(newValue);
                if (newValue) setItemError("");
              }}
              options={inventory[inventoryKey] || []}
              getOptionLabel={(option) => option.item_name}
              isOptionEqualToValue={(option, value) =>
                option.item_name === value?.item_name
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Stir Type"
                  variant="outlined"
                  error={!!itemError}
                  helperText={itemError}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <RepeatIcon color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
              disableClearable
            />

            <FormControl fullWidth variant="outlined">
              <TextField
                label="Number of times"
                type="number"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  if (e.target.value) setError("");
                }}
                error={!!error}
                helperText={
                  error ||
                  `Enter a value between ${minQuantity} and ${maxQuantity}`
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">times</InputAdornment>
                  ),
                }}
                inputProps={{
                  min: minQuantity,
                  max: maxQuantity,
                }}
                color="primary"
                sx={{ mt: 1 }}
              />
            </FormControl>

            <Box
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                mt: 2,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                size={isMobile ? "large" : "medium"}
                sx={{
                  minWidth: isMobile ? "100%" : "120px",
                  py: 1.2,
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: "bold",
                }}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : null
                }
              >
                {isSubmitting ? "Submitting..." : "Apply Settings"}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Fade>
  );
}
